// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getPurchaseOrderFiles } from '@/purchase-order/api/get-purchase-order-files';
import { purchaseOrderFilesQueryKey } from '@/purchase-order/query-keys';
import { useQuery } from '@tanstack/react-query';

interface UsePurchaseOrderFilesOptions {
  /** The key of the purchase order to fetch files for */
  purchaseOrderKey: string;
}

/**
 * Hook to fetch files associated with a purchase order.
 * Uses a regular query (not suspense) to allow graceful error handling
 * without crashing the entire page.
 *
 * @param options - Options including the purchase order key
 * @returns The files, loading state, error state, and query methods
 */
export function usePurchaseOrderFiles(options: UsePurchaseOrderFilesOptions) {
  const { data, isLoading, isError, error, refetch, ...query } = useQuery({
    queryKey: purchaseOrderFilesQueryKey(options.purchaseOrderKey),
    queryFn: () => getPurchaseOrderFiles(options.purchaseOrderKey),
  });

  return {
    files: data?.data ?? [],
    isLoading,
    isError,
    error,
    refetch,
    ...query,
  };
}
